import { useState } from 'react'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Grid, Typography } from '@mui/material'

import BatchDetailsGrid from 'components/bookingInvoiceBatchDetails/BatchDetailsGrid'
import { FplDataGrid } from 'components/common'
import {
  useRenewAllBatchesMutation,
  useRenewBatchesMutation,
  useSendBatchesToXeroMutation,
} from 'generated/graphql'
import { GET_BOOKING_INVOICE_BATCHES } from 'graphql/queries'
import { useGetInvoiceBatchesColummns } from 'hooks/accountsPayable/useGetInvoiceBatchesColumns'
import { usePermission } from 'providers'

import { useInvoiceBatchFilter } from './hooks/useInvoiceBatchFilter'
import SendToXeroDialog from './SendToXeroDialog'

const InvoiceBatches = () => {
  const [isAllowedToRenewBatches] = usePermission('RenewBatches')
  const [isAllowedToSendBatches] = usePermission('SendBatch')
  const [selectedBatchRow, setSelectedBatchRow] = useState<null | number>(null)
  const [selectedBatchIds, setSelectedBatchIds] = useState<Array<string>>([])
  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const [queuedBatchIds, setQueuedBatchIds] = useState<Array<string>>([])
  const [lastQueuedIds, setLastQueuedIds] = useState<Array<string>>([])

  const [renewBatches, { loading: renewingBatches }] = useRenewBatchesMutation()
  const [renewAllBatches, { loading: renewingAllBatches }] = useRenewAllBatchesMutation()
  const [sendBatchesToXero, { loading: sendingBatches }] = useSendBatchesToXeroMutation()

  const invoiceBatchFilter = useInvoiceBatchFilter()

  const handleRowClickAllGrid = (row) => {
    setSelectedBatchRow(row.id)
  }

  const handleRenewBatchesClick = () => {
    renewBatches({
      variables: {
        input: {
          ids: selectedBatchIds.map((i) => Number(i)),
        },
      },
    })
  }

  const handleRenewAllBatchesClick = () => renewAllBatches()

  const handleSendBatchesToXeroClick = () => {
    const idsBeingSent = [...selectedBatchIds]
    sendBatchesToXero({
      variables: {
        batchIds: idsBeingSent.map((i) => Number(i)),
      },
      onCompleted: () => {
        setLastQueuedIds(idsBeingSent)
        setQueuedBatchIds((prev) => [...new Set([...prev, ...idsBeingSent])])
        setSelectedBatchIds([])
        setSendDialogOpen(true)
      },
    })
  }

  const columns = useGetInvoiceBatchesColummns()

  const leftSide = (
    <>
      {isAllowedToRenewBatches && (
        <>
          <Grid item>
            <LoadingButton
              variant='outlined'
              onClick={handleRenewAllBatchesClick}
              loading={renewingAllBatches}>
              Renew All
            </LoadingButton>
          </Grid>

          <Grid item>
            <LoadingButton
              variant='outlined'
              disabled={selectedBatchIds.length === 0}
              onClick={handleRenewBatchesClick}
              loading={renewingBatches}>
              Renew
            </LoadingButton>
          </Grid>
        </>
      )}

      {isAllowedToSendBatches && (
        <Grid item>
          <LoadingButton
            variant='outlined'
            disabled={selectedBatchIds.length === 0}
            onClick={handleSendBatchesToXeroClick}
            loading={sendingBatches}>
            Send to Xero
          </LoadingButton>
        </Grid>
      )}
    </>
  )

  return (
    <Box>
      {!selectedBatchRow && (
        <FplDataGrid
          query={GET_BOOKING_INVOICE_BATCHES}
          refetchWithDelay
          filter={invoiceBatchFilter}
          checkboxSelection
          entityName='bookingInvoiceBatches'
          toolbar={{
            caption: 'Invoice Batches',
            leftSide: isAllowedToRenewBatches || isAllowedToSendBatches ? leftSide : undefined,
          }}
          columns={columns}
          selectionModel={selectedBatchIds}
          isRowSelectable={(params) =>
            !params.row.isSentToXero && !queuedBatchIds.includes(String(params.row.id))
          }
          onRowClick={(e) => handleRowClickAllGrid(e)}
          onSelectionModelChange={(e) => setSelectedBatchIds(e as string[])}
        />
      )}

      {selectedBatchRow && (
        <>
          <Button sx={{ marginRight: '15px' }} onClick={() => setSelectedBatchRow(null)}>
            <ArrowBackIcon color='primary'></ArrowBackIcon>
            <Typography color={'primary'}>Back</Typography>
          </Button>

          <BatchDetailsGrid batchId={selectedBatchRow} />
        </>
      )}

      <SendToXeroDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        queuedIds={lastQueuedIds}
      />
    </Box>
  )
}

export default InvoiceBatches